import type { PaymentRoutesTranslations } from "../interfaces/paymentRoutes";

export const paymentRoutesTranslations: PaymentRoutesTranslations = {
  // Página
  page_title: "Corredores y proveedores",
  page_subtitle:
    "Qué corredores existen y con qué proveedor y modelo económico se ejecuta cada entrada y salida. Un corredor sin ruta rechaza todas sus operaciones.",
  refresh_button: "Actualizar",
  refresh_aria: "Recargar los corredores",
  button_new_route: "Nueva ruta",

  // Estados de carga
  truncated_notice:
    "Se cargaron {{loaded}} de {{total}} rutas. La cobertura puede estar incompleta hasta cargarlas todas.",
  error_load: "No se pudo cargar el catálogo de corredores.",
  error_load_detail: "No se muestra ninguna fila: el estado de los corredores nunca se inventa.",
  error_retry: "Reintentar",

  // Indicadores
  kpi_routed_label: "Corredores enrutados",
  kpi_routed_meta: "de {{total}} corredores operativos",
  kpi_missing_label: "Sin ruta",
  kpi_missing_meta: "Rechazan todas sus entradas y salidas",
  kpi_missing_ok: "Todos los corredores tienen ruta",
  kpi_blocked_label: "Bloqueados",
  kpi_blocked_meta: "Ruta inactiva ocupando el corredor",
  kpi_blocked_ok: "Ninguna ruta inactiva",
  kpi_unsupported_label: "Proveedor sin capacidad",
  kpi_unsupported_meta: "Sus salidas quedarán detenidas al despachar",
  kpi_unsupported_ok: "Todas las rutas tienen proveedor capaz",

  // Estados de un corredor
  status_routed: "Enrutado",
  status_missing: "Sin ruta",
  status_blocked: "Bloqueado",
  status_unsupported: "Sin capacidad",
  consequence_routed: "El corredor opera con normalidad.",
  consequence_missing:
    "No hay ruta configurada: todas las entradas y salidas de este corredor se rechazan antes de calcular precios.",
  consequence_blocked:
    "Hay una ruta inactiva ocupando el corredor: no acepta operaciones y tampoco admite otra ruta. No se puede reactivar: hay que eliminarla y crear la nueva.",
  consequence_unsupported:
    "El proveedor configurado no cubre este corredor. Las salidas reservan fondos y quedan detenidas al despachar; aparecen en Rescate de dispersiones.",

  // Tabla
  th_corridor: "Corredor",
  th_direction: "Dirección",
  th_status: "Estado",
  th_model: "Modelo económico",
  th_provider: "Proveedor",
  provider_corridors: "Enruta {{count}} corredor(es)",
  provider_unknown: "Proveedor no reconocido",
  provider_empty: "Sin proveedor",
  offcatalog_badge: "Fuera del catálogo",
  offcatalog_notice:
    "Estas rutas apuntan a corredores que ningún flujo del dashboard puede alcanzar. Se muestran porque existen en la configuración, no porque se puedan usar.",

  // Acciones
  action_view: "Ver",
  action_create: "Crear ruta",
  action_switch: "Cambiar de proveedor",
  action_delete: "Eliminar ruta",

  // Vacío y contexto
  empty_title: "No hay corredores configurados",
  empty_description:
    "Sin rutas, el motor de pagos rechaza todas las entradas y salidas. Creá la primera ruta para habilitar un corredor.",
  why_banner:
    "Cada ruta define un corredor: sin ella no hay operaciones, y si apunta a un proveedor que no lo cubre, las salidas se quedan detenidas con los fondos ya reservados.",

  // Panel lateral
  drawer_subtitle: "Detalle de la ruta configurada para este corredor.",
  drawer_section_corridor: "Corredor",
  drawer_section_provider: "Proveedor",
  drawer_section_audit: "Registro",
  drawer_field_status: "Estado",
  drawer_field_model: "Modelo económico",
  drawer_field_capability: "Capacidad para este corredor",
  drawer_field_id: "Identificador de la ruta",
  drawer_field_created: "Creada",
  drawer_field_updated: "Actualizada",
  capability_ok: "El proveedor declara este corredor",
  capability_missing: "El proveedor no declara este corredor",
  drawer_link_fees: "Ver tarifas de este corredor",
  drawer_copy_id: "Copiar identificador",
  drawer_copied: "Identificador copiado",

  // Diálogo de creación
  create_title: "Nueva ruta",
  create_subtitle: "Habilitá un corredor eligiendo quién lo ejecuta y con qué modelo económico.",
  create_section_corridor: "Corredor",
  create_section_provider: "Proveedor",
  create_section_model: "Modelo económico",
  create_corridor_label: "Corredor",
  create_corridor_placeholder: "Elegí un corredor",
  create_corridor_helper:
    "El riel y la moneda los define el corredor: el motor fija uno de cada uno por país.",
  create_direction_label: "Dirección",
  direction_payin_description: "Dinero que entra: cobros de los clientes.",
  direction_payout_description: "Dinero que sale: dispersiones a beneficiarios.",
  create_provider_label: "Quién ejecuta el corredor",
  create_provider_helper:
    "Sólo se ofrecen los proveedores que declaran este corredor. Un proveedor que no lo cubre dejaría las salidas detenidas.",
  provider_option_label: "Proveedor del corredor",
  provider_option_coverage: "Cubre: {{corridors}}",
  provider_option_meta: "Ya enruta {{count}} corredor(es)",
  provider_option_meta_none: "Todavía no enruta ningún corredor",
  provider_pick_corridor_first:
    "Elegí primero el corredor y la dirección para ver qué proveedor puede ejecutarlo.",
  create_provider_none_title: "Ningún proveedor cubre este corredor",
  create_provider_none_description:
    "No hay proveedor integrado capaz de ejecutarlo, así que una ruta aquí sólo dejaría las operaciones detenidas. Hay que integrarlo antes de habilitar el corredor.",
  create_model_label: "Quién paga el costo del proveedor",
  create_model_helper: "Decide la fórmula del neto y los asientos contables de cada operación del corredor.",
  create_summary_title: "Qué va a pasar",
  create_summary_sentence: "Las operaciones de {{corridor}} se ejecutarán con {{model}}.",
  create_summary_pick_corridor: "Elegí un corredor y una dirección para ver el resultado.",
  create_warn_exists:
    "Este corredor ya tiene una ruta. El motor admite una sola por corredor: para reasignarlo, usá «Cambiar de proveedor».",
  create_warn_blocked:
    "Este corredor tiene una ruta inactiva que lo mantiene ocupado. Hay que eliminarla antes de crear la nueva.",
  create_next_step:
    "La ruta queda activa de inmediato. Revisá que el corredor tenga tarifas configuradas: sin ellas también rechaza operaciones.",
  submit: "Crear ruta",
  cancel: "Cancelar",
  validation_corridor_required: "Elegí el corredor y la dirección.",
  validation_provider_required: "Elegí el proveedor que va a ejecutar el corredor.",

  // Diálogo de eliminación
  delete_title: "Eliminar la ruta del corredor",
  delete_semantics: "La ruta se elimina y el corredor queda libre para configurar otra.",
  delete_consequence:
    "Desde ese momento, {{corridor}} rechaza todas sus operaciones hasta que exista una ruta nueva.",
  delete_consequence_blocked:
    "La ruta está inactiva, así que {{corridor}} ya rechaza sus operaciones: eliminarla no empeora nada y es el único modo de liberar el corredor.",
  delete_frees_corridor:
    "Eliminarla es el paso previo obligatorio para apuntar el corredor a otro proveedor.",
  delete_confirm: "Eliminar ruta",
  delete_cancel: "Cancelar",

  // Diálogo de cambio de proveedor
  switch_title: "Cambiar de proveedor",
  switch_subtitle:
    "El motor admite una sola ruta por corredor, así que el cambio elimina la actual y crea la nueva.",
  switch_current_label: "Ruta actual",
  switch_gap_warning:
    "Entre eliminar la ruta actual y crear la nueva, el corredor queda unos instantes sin ruta: las operaciones que caigan en esa ventana se rechazan.",
  switch_no_alternative:
    "No hay otro proveedor integrado que cubra este corredor, así que no hay a dónde cambiarlo.",
  switch_confirm: "Cambiar proveedor",
  switch_failed_title: "El corredor quedó sin ruta",
  switch_failed_detail:
    "Se eliminó la ruta anterior pero no se pudo crear la nueva, así que el corredor está rechazando operaciones. Reintentá la creación.",
  switch_retry: "Reintentar la creación",

  // Avisos
  toast_created: "Ruta creada: {{corridor}} ya opera.",
  toast_deleted: "Ruta eliminada: {{corridor}} queda sin ruta.",
  toast_switched: "Proveedor cambiado: {{corridor}} ya opera con el nuevo.",
  error_delete_missing: "La ruta ya no existía. Se actualizó la vista.",
  error_no_write_permission: "No tenés permiso para modificar los corredores.",
  error_create: "No se pudo crear la ruta.",
  error_delete: "No se pudo eliminar la ruta.",
};
